import React, { use, useCallback, useState } from "react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import { useI18n } from "../../hooks/i18n-hooks";
import { Gender } from "../../__generated__/graphql";
import { useMutation } from "@apollo/client/react";
import Loading from "../../components/Loading";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useTheme } from "../../hooks/use-theme";
import TextField from "../../components/TextField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SelectField from "../../components/SelectField";
import { isValidAVSNumber } from "../../avs";
import OptionsField from "../../components/OptionsField";
import { BodyMediumText } from "../../components/FormattedText";
import ScreenCard from "../ScreenCard";
import { avsMask, fromMaybe } from "../../utils";
import { Pressable, StyleSheet } from "react-native";
import DateField from "../../components/DateField";
import ListItem from "../../ui_atoms/ListItem";
import Gap from "../../ui_atoms/Gap";
import { FIELD_HEIGHT } from "../../components/BaseField";
import { useCloseEditor } from "./use-close-editor";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";
import { isAdult } from "../../hooks/use-get-is-worker-profile-complete";
import { ChildFormData, toChildrenInput } from "./worker-profile-changes";

type FormData = {
    hasChildren: true;
    children: ChildFormData[];
};

type ChildrenEditorScreenProps = StackScreenProps<RootStackParamList, "ChildrenEdit">;

const ChildrenEditorScreen = ({ navigation, route }: ChildrenEditorScreenProps) => {
    const { profileId } = route.params || {};
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const { user, refreshUser } = use(AuthManagerContext);

    const [hasChildren, setHasChildren] = useState<boolean | undefined>(() =>
        fromMaybe(user?.workerProfile?.hasChildren),
    );

    const initialChildren = user?.workerProfile?.children;

    const [
        updateWorkerProfile,
        { loading: updateLoading, error: updateError, reset: updateReset },
    ] = useMutation(
        UPDATE_WORKER_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );

    useApolloErrorSnackbar(
        updateError,
        updateReset,
        i18n.t("screens.profile_editor.civil_state.errors.update_worker_profile_error"),
    );

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: {
                            hasChildren: hasChildren,
                            children: hasChildren ? toChildrenInput(data.children) : [],
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // this is handled by updateError in useMutation
            }
        },
        [hasChildren, navigation, refreshUser, updateWorkerProfile],
    );

    const {
        handleSubmit,
        control,
        watch,
        formState: { isValid, isDirty },
        setValue,
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            children: initialChildren || undefined,
        },
    });

    const children = watch("children");

    const { chainInputFocus } = useChainInputFocus();

    const onAddChildren = useCallback(() => {
        setValue("children", [...(children || []), {}], { shouldValidate: true });
    }, [children, setValue]);

    useCloseEditor(isDirty && !updateLoading);

    const isScreenValid = hasChildren ? isValid && isDirty : hasChildren === false;

    const onHasChildrenChange = useCallback(
        (newValue?: boolean) => {
            setHasChildren(newValue);
            if (newValue && !children?.length) {
                setValue("children", [{}]);
            }
        },
        [children?.length, setValue],
    );

    if (!user) {
        return (
            <VStack>
                <Loading />
            </VStack>
        );
    }

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: updateLoading,
                    isValid: isScreenValid,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            {/* zIndex 1 is needed to be sure the popeup of the date field will be displayed above items that are bellow this VStack */}
            <VStack h="2/3" gap={1} style={{ zIndex: 1 }}>
                <VStack gap={0.25}>
                    <BodyMediumText>
                        {i18n.t("screens.profile_editor.children.do_you_have_children")}
                    </BodyMediumText>
                    <OptionsField
                        value={hasChildren}
                        data={[
                            { label: i18n.t("yes"), value: true },
                            { label: i18n.t("no"), value: false },
                        ]}
                        onSelect={onHasChildrenChange}
                        shrinked
                    />
                </VStack>
                {hasChildren &&
                    children?.map((child, index) => {
                        const childNb = index + 1;

                        const onDeleteChild = () => {
                            const newChildren = (children || []).filter((_, i) => i !== index);
                            setValue("children", newChildren, { shouldValidate: true });
                            if (newChildren.length === 0) {
                                setHasChildren(false);
                            }
                        };

                        return (
                            <React.Fragment key={index}>
                                <Controller
                                    control={control}
                                    name={`children.${index}.firstName`}
                                    rules={{ required: true }}
                                    render={({
                                        field: { onChange, onBlur, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            isSelfContained
                                            {...chainInputFocus(name)}
                                            style={{
                                                borderBottomRightRadius: 0,
                                                borderBottomLeftRadius: 0,
                                            }}
                                            error={error?.message}
                                            value={value || ""}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_first_name",
                                            )} ${childNb}`}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`children.${index}.lastName`}
                                    rules={{ required: true }}
                                    render={({
                                        field: { onChange, onBlur, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            {...chainInputFocus(name)}
                                            isSelfContained
                                            style={styles.stackedField}
                                            error={error?.message}
                                            value={value || ""}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_last_name",
                                            )} ${childNb}`}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`children.${index}.birthdate`}
                                    rules={{ required: true }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <DateField
                                            isSelfContained
                                            style={styles.stackedField}
                                            error={error?.message}
                                            value={value ? new Date(value) : undefined}
                                            maximumDate={new Date()}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_birth_date",
                                            )} ${childNb}`}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`children.${index}.gender`}
                                    rules={{ required: true }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <SelectField<Gender>
                                            isSelfContained
                                            error={error?.message}
                                            value={value || undefined}
                                            style={styles.stackedField}
                                            data={[Gender.Male, Gender.Female].map((v) => ({
                                                value: v,
                                                label: i18n.t(`user.gender_kid_${v}`),
                                            }))}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_gender",
                                            )} ${childNb}`}
                                            onSelect={(e) => {
                                                onChange(e.value);
                                            }}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`children.${index}.avsNumber`}
                                    rules={{
                                        required: false, // Children AVS are never required (could live abroad, for example)
                                        validate: (v) => {
                                            if (!v) {
                                                // allow empty field
                                                return true;
                                            } else if (isValidAVSNumber(v)) {
                                                if (
                                                    !children.find(
                                                        (e, i) => i !== index && e.avsNumber === v,
                                                    )
                                                ) {
                                                    return true;
                                                } else {
                                                    return i18n.t(
                                                        "screens.profile_editor.children.errors.not_unique_avs",
                                                    );
                                                }
                                            } else {
                                                return i18n.t("errors.avs_number");
                                            }
                                        },
                                    }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            isSelfContained
                                            error={error?.message}
                                            style={styles.stackedField}
                                            value={value || ""}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_avs",
                                            )} ${childNb} (${i18n.t("optional").toLowerCase()})`}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                            keyboardType="numeric"
                                            mask={avsMask}
                                            maskAutoComplete
                                        />
                                    )}
                                />
                                <Pressable style={styles.removeSubButton} onPress={onDeleteChild}>
                                    <BodyMediumText color={colors.error}>
                                        {i18n.t("screens.profile_editor.children.remove_children")}{" "}
                                        {childNb}
                                    </BodyMediumText>
                                </Pressable>
                                {/* A minor always counts for withholding tax: only asked from 18. The
                                    child's fields share a single slot of the parent VStack, so its
                                    gap does not separate the question from the remove button. */}
                                {isAdult(child.birthdate) && (
                                    <VStack gap={0.25} top={1}>
                                        <BodyMediumText>
                                            {i18n.t(
                                                "screens.profile_editor.children.in_first_education",
                                                { name: child.firstName || childNb },
                                            )}
                                        </BodyMediumText>
                                        <Controller
                                            control={control}
                                            name={`children.${index}.inFirstEducation`}
                                            rules={{
                                                validate: (v) => typeof v === "boolean",
                                            }}
                                            render={({
                                                field: { onChange, onBlur, value },
                                                fieldState: { error },
                                            }) => (
                                                <OptionsField
                                                    value={value ?? undefined}
                                                    data={[
                                                        { label: i18n.t("yes"), value: true },
                                                        { label: i18n.t("no"), value: false },
                                                    ]}
                                                    onSelect={onChange}
                                                    onBlur={onBlur}
                                                    error={error?.message}
                                                    shrinked
                                                />
                                            )}
                                        />
                                    </VStack>
                                )}
                            </React.Fragment>
                        );
                    })}
            </VStack>
            {hasChildren && (
                <>
                    <Gap gap={1} />
                    <ListItem
                        labels={[
                            {
                                text: i18n.t("screens.profile_editor.children.add_children"),
                                mode: "cta",
                            },
                        ]}
                        onPress={onAddChildren}
                        rightIcon="add-large"
                        rightActionMode="action_simple"
                    />
                </>
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { colors, roundness } = useTheme();

    return StyleSheet.create({
        removeSubButton: {
            borderWidth: 2,
            borderColor: colors.onPrimary,
            height: FIELD_HEIGHT,
            borderTopWidth: 0,
            borderBottomRightRadius: roundness,
            borderBottomLeftRadius: roundness,
            alignItems: "center",
            justifyContent: "center",
        },
        stackedField: {
            borderTopRightRadius: 0,
            borderTopLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            marginTop: -2,
        },
    });
};

export default ChildrenEditorScreen;
